import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { RestartAlt as ReopenIcon } from "@mui/icons-material";
import { dt } from "../../../../shared/themes/redesignTokens";
import { EnrichedServiceReport } from "../../store/serviceReportsSlice";
import serviceReportsApi from "../../api/serviceReportsApi";

// ─── Props ──────────────────────────────────────────────────

interface BulkReopenDialogProps {
  open: boolean;
  items: EnrichedServiceReport[];
  onClose: () => void;
  onReopened: (count: number) => void;
  translate: (key: string) => string;
}

// ─── Component ──────────────────────────────────────────────

const BulkReopenDialog: React.FC<BulkReopenDialogProps> = ({
  open,
  items,
  onClose,
  onReopened,
  translate,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setError("");
      setSubmitting(false);
      setProgress(0);
    }
  }, [open]);

  // Inject keyframes
  useEffect(() => {
    const id = "bulk-reopen-dialog-keyframes";
    if (!document.getElementById(id)) {
      const style = document.createElement("style");
      style.id = id;
      style.textContent = `
        @keyframes bulkReopenSlideUp {
          0% { opacity: 0; transform: translateY(24px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes bulkReopenBackdropIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError("");

    let reopened = 0;
    for (let i = 0; i < items.length; i++) {
      const sr = items[i].sr;
      if (!sr.number) continue;
      setProgress(i + 1);
      try {
        await serviceReportsApi.reopenSr(sr.number);
        reopened++;
      } catch {
        // Continue with others even if one fails
      }
    }

    if (reopened === 0) {
      setError(translate("serviceReports.bulk.reopenFailedAll"));
      setSubmitting(false);
    } else {
      onReopened(reopened);
    }
  };

  if (!open) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1400,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Backdrop */}
      <Box
        onClick={submitting ? undefined : onClose}
        sx={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(12,17,23,0.25)",
          animation: "bulkReopenBackdropIn 0.2s ease",
        }}
      />

      {/* Dialog */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "420px",
          mx: "16px",
          backgroundColor: "#FFFFFF",
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)",
          animation: "bulkReopenSlideUp 0.3s ease",
        }}
      >
        {/* Blue accent bar */}
        <Box
          sx={{
            height: "4px",
            background: "linear-gradient(90deg, #3B82F6 0%, #60A5FA 100%)",
          }}
        />

        {/* Content */}
        <Box sx={{ padding: "32px 28px 24px", textAlign: "center" }}>
          {/* Icon */}
          <Box
            sx={{
              width: "48px",
              height: "48px",
              borderRadius: "50%",
              backgroundColor: "rgba(59,130,246,0.10)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              mb: "16px",
            }}
          >
            <ReopenIcon sx={{ fontSize: "24px", color: "#3B82F6" }} />
          </Box>

          {/* Title */}
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "18px",
              fontWeight: 700,
              color: "#0C1117",
              mb: "12px",
            }}
          >
            {translate("serviceReports.bulk.reopenTitle").replace("{{count}}", String(items.length))}
          </Box>

          {/* Description */}
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "13px",
              color: "#6B7280",
              lineHeight: 1.6,
              mb: "20px",
            }}
          >
            {translate("serviceReports.bulk.reopenDescription")}
          </Box>

          {/* Error message */}
          {error && (
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "12px",
                color: "#DC2626",
                backgroundColor: "rgba(220,38,38,0.06)",
                borderRadius: "8px",
                padding: "8px 12px",
                mb: "16px",
              }}
            >
              {error}
            </Box>
          )}

          {/* Progress indicator */}
          {submitting && (
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 500,
                color: "#3B82F6",
                textAlign: "center",
                mb: "16px",
              }}
            >
              {translate("serviceReports.bulk.reopeningProgress")
                .replace("{{current}}", String(progress))
                .replace("{{total}}", String(items.length))}
            </Box>
          )}

          {/* Buttons */}
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "16px", mt: error || submitting ? "0px" : "4px" }}>
            <Box
              onClick={submitting ? undefined : onClose}
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 500,
                color: "#8C9099",
                cursor: submitting ? "default" : "pointer",
                padding: "8px 16px",
                borderRadius: "8px",
                transition: "color 0.15s ease",
                opacity: submitting ? 0.5 : 1,
                "&:hover": submitting ? {} : { color: "#4A4F57" },
              }}
            >
              {translate("serviceReports.actions.cancel")}
            </Box>
            <Box
              onClick={handleSubmit}
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 600,
                color: "#FFFFFF",
                background: submitting
                  ? "#93C5FD"
                  : "linear-gradient(135deg, #3B82F6, #2563EB)",
                borderRadius: "24px",
                padding: "10px 28px",
                cursor: submitting ? "default" : "pointer",
                transition: "all 0.2s ease",
                boxShadow: submitting ? "none" : "0 2px 8px rgba(59,130,246,0.25)",
                "&:hover": submitting
                  ? {}
                  : {
                      background: "linear-gradient(135deg, #2563EB, #3B82F6)",
                      boxShadow: "0 4px 12px rgba(59,130,246,0.30)",
                    },
              }}
            >
              {submitting
                ? translate("serviceReports.bulk.reopening")
                : translate("serviceReports.bulk.reopenButton")}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BulkReopenDialog;
